// Shared odometer-style number animation for loon balances and similar counters.

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utilities/cn';

const DEFAULT_BASE_DELAY = 350;
const DEFAULT_STAGGER = 60;
const ODOMETER_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const DIGIT_ROLL_MS = 550;
// How long a column that's only there for alignment lingers before collapsing.
const PAD_FADE_MS = 400;

type OdometerDigitProps = {
  digit: number;
  startDigit?: number;
  delay: number;
  animationKey: string;
};

type AnimatedOdometerNumberProps = {
  text: string;
  startFrom?: string;
  animationKey?: string;
  baseDelay?: number;
  stagger?: number;
  className?: string;
};

const OdometerDigit: React.FC<OdometerDigitProps> = ({
  digit,
  startDigit,
  delay,
  animationKey,
}) => {
  const [started, setStarted] = useState(false);
  const prevDigitRef = useRef(startDigit ?? 0);

  useEffect(() => {
    setStarted(false);
    const timeout = window.setTimeout(() => setStarted(true), delay);
    return () => {
      window.clearTimeout(timeout);
      prevDigitRef.current = digit;
    };
  }, [delay, digit, animationKey]);

  return (
    <span className="inline-block h-[1em] overflow-hidden align-bottom">
      <span
        className="flex flex-col"
        style={{
          transform: started
            ? `translateY(-${digit * 10}%)`
            : `translateY(-${(startDigit ?? prevDigitRef.current) * 10}%)`,
          transition: started
            ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)'
            : 'none',
        }}
      >
        {ODOMETER_DIGITS.map((odometerDigit) => (
          <span
            key={odometerDigit}
            className="block h-[1em] text-center leading-none"
          >
            {odometerDigit}
          </span>
        ))}
      </span>
    </span>
  );
};

const isPlainNumber = (value: string) => /^\d+$/.test(value);

const AnimatedOdometerNumber: React.FC<AnimatedOdometerNumberProps> = ({
  text,
  startFrom,
  animationKey,
  baseDelay = DEFAULT_BASE_DELAY,
  stagger = DEFAULT_STAGGER,
  className,
}) => {
  const resolvedAnimationKey = animationKey ?? text;
  const rootClass = cn(
    'animated-odometer inline-flex items-end whitespace-nowrap',
    className
  );

  // Remembered so every caller gets digit-count alignment for free; an
  // explicit startFrom still wins (it survives remounts, this doesn't).
  const previousTextRef = useRef(text);
  const from = startFrom ?? previousTextRef.current;
  useEffect(() => {
    previousTextRef.current = text;
  }, [text]);

  // Counting across a change in digit count is the tricky case: 100 → 89 has
  // to roll every column to 089 and only then drop the dead leading zero, and
  // 97 → 105 has to keep 97 in place and expand a fresh hundreds column in.
  // Anything with separators or signs keeps the simple per-character path.
  const isAligned = isPlainNumber(text) && isPlainNumber(from);
  const width = isAligned ? Math.max(text.length, from.length) : text.length;
  // Leading columns the TEXT doesn't need (shrink) collapse out after the roll.
  const padCount = isAligned ? width - text.length : 0;
  // Leading columns the FROM value didn't have (growth) expand in as it starts.
  const growthCount = isAligned ? width - from.length : 0;

  // `atEnd` walks each roll from its opening layout to its resting one exactly
  // once. Growth starts with the new column collapsed and expands it as the
  // roll begins; shrink starts with the dead column expanded and drops it once
  // the roll lands. Reset synchronously on a key change (below) so the first
  // painted frame is already the opening layout — otherwise the column shows
  // expanded for one frame and the number visibly bounces its digit count.
  const [atEnd, setAtEnd] = useState(false);
  const rollKeyRef = useRef<string | null>(null);
  if (rollKeyRef.current !== resolvedAnimationKey) {
    rollKeyRef.current = resolvedAnimationKey;
    if (atEnd) {
      setAtEnd(false);
    }
  }
  useEffect(() => {
    if (padCount === 0 && growthCount === 0) {
      return;
    }
    // Growth flips at the roll's start (column grows in with the digit);
    // shrink flips once every column has finished rolling.
    const flipAt =
      growthCount > 0
        ? baseDelay
        : baseDelay + (width - 1) * stagger + DIGIT_ROLL_MS;
    const timeout = window.setTimeout(() => setAtEnd(true), flipAt);
    return () => window.clearTimeout(timeout);
  }, [resolvedAnimationKey, padCount, growthCount, baseDelay, stagger, width]);

  if (!isAligned) {
    const startDigits = startFrom
      ? startFrom.replace(/\D/g, '').split('').map(Number)
      : undefined;
    const textDigits = text.replace(/\D/g, '').split('');
    let digitIndex = 0;

    return (
      <span className={rootClass}>
        {text.split('').map((character, index) => {
          if (/\d/.test(character)) {
            // Align start digits from the right (ones place)
            const currentDigitIndex = digitIndex++;
            const startDigit = startDigits
              ? startDigits[
                  startDigits.length - textDigits.length + currentDigitIndex
                ]
              : undefined;

            return (
              <OdometerDigit
                key={`digit-${index}`}
                digit={Number(character)}
                startDigit={startDigit}
                delay={baseDelay + index * stagger}
                animationKey={resolvedAnimationKey}
              />
            );
          }

          return (
            <span key={`separator-${index}`} className="inline-block">
              {character}
            </span>
          );
        })}
      </span>
    );
  }

  const paddedText = text.padStart(width, '0');
  const paddedFrom = from.padStart(width, '0');

  return (
    <span className={rootClass}>
      {paddedText.split('').map((character, index) => {
        // A leading column eases its width between 0 and 1em so the number
        // never jumps sideways: a pad column (dead weight the text sheds) is
        // open until the roll ends, a growth column (a fresh column the value
        // gains) is closed until it starts.
        const isPad = index < padCount;
        const isGrowth = index < growthCount;
        const isFlexColumn = isPad || isGrowth;
        const isOpen = isGrowth ? atEnd : !atEnd;

        return (
          <span
            key={`column-${index}`}
            className="inline-block overflow-hidden align-bottom"
            style={
              isFlexColumn
                ? {
                    opacity: isOpen ? 1 : 0,
                    maxWidth: isOpen ? '1em' : 0,
                    transition: `opacity ${PAD_FADE_MS}ms ease-out, max-width ${PAD_FADE_MS}ms ease-out`,
                  }
                : undefined
            }
          >
            <OdometerDigit
              digit={Number(character)}
              startDigit={Number(paddedFrom[index])}
              delay={baseDelay + index * stagger}
              animationKey={resolvedAnimationKey}
            />
          </span>
        );
      })}
    </span>
  );
};

export default AnimatedOdometerNumber;
